import * as Sentry from '@sentry/vue'

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()
  const {
    public: { sentry }
  } = useRuntimeConfig()

  if (!sentry.dsn) {
    return
  }

  const { vueApp } = nuxtApp

  Sentry.init({
    app: vueApp,
    dsn: sentry.dsn,
    environment: sentry.environment,
    denyUrls: [/_nuxt\/.*\.js$/, /.*404.*/, /.*page-not-found.*/],
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false
      })
    ],

    // Configure this whole part as you need it!

    tracesSampleRate: 0.2, // Change in prod

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      'https://dev.caseclub.in.th',
      'https://qa.caseclub.in.th',
      'https://caseclub.in.th'
    ],

    replaysSessionSampleRate: 1.0, // Change in prod
    replaysOnErrorSampleRate: 1.0, // Change in prod if necessary

    beforeSend(event, hint) {
      const error = hint?.originalException as Error

      // Ignore 404 errors
      if (
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('404') ||
        error?.message?.includes('Page not found') ||
        error?.message?.toLowerCase().includes('cannot find static asset')
      ) {
        return null
      }
      if (event?.request?.url?.includes('404')) {
        return null
      }
      // Ignore Nuxt Static Assets (_nuxt/*.js) errors
      if (event.request?.url?.match(/\/_nuxt\/.*\.js$/)) {
        return null
      }
      // Check if it is an exception, and if so, log it.
      if (event.exception) {
        console.error(
          `[Exeption handled by Sentry]: (${hint.originalException})`,
          { event, hint }
        )
      }
      // Continue sending to Sentry
      return event
    }
  })

  vueApp.mixin(
    Sentry.createTracingMixins({
      trackComponents: true,
      timeout: 2000,
      hooks: ['activate', 'mount', 'update']
    })
  )
  Sentry.attachErrorHandler(vueApp, {
    logErrors: false,
    attachProps: true,
    trackComponents: true,
    timeout: 2000,
    hooks: ['activate', 'mount', 'update']
  })

  return {
    provide: {
      sentrySetContext: Sentry.setContext,
      sentrySetUser: Sentry.setUser,
      sentrySetTag: Sentry.setTag,
      sentryAddBreadcrumb: Sentry.addBreadcrumb,
      sentryCaptureException: Sentry.captureException
    }
  }
})
